/**
 * ImmolateSkillEffect — Fire tree Apotheosis: the living flame transformation.
 *
 * A 10-second timed-state active. On activation the player becomes immune
 * to damage (invincibility buff), moves at full speed, and trails fire
 * zones with every step. On expiry (natural or early cancel) a burst
 * zone detonates at the player's feet.
 *
 * **Activation:** Apply `invincibility` player buff for 10000ms. Break Hide.
 * Set up movement trail tracking from the current player position.
 *
 * **Active loop (per-frame):** Query player position. When the player moves
 * ≥ 1 tile (8 pixels) from the last trail-zone anchor:
 *   - Create `fire_zone` (circle, 2-tile radius, 4000ms, burning intensity 2,
 *     500ms tick) at the previous anchor position.
 *   - Advance anchor to current position.
 * This rate-limits trail zones to one per tile of movement, preventing
 * zone flooding at high frame rates.
 *
 * **Expiry (10000ms elapsed or early deactivate):** Create a burst
 * `fire_zone` (circle, 4-tile radius, 2000ms, burning intensity 4, 500ms
 * tick) at the current player position. Start very-long cooldown (4000ms).
 *
 * **Very long cooldown** after expiry or early cancel.
 * **Apotheosis gate:** Both `cultivate_passive` and `shape_passive` parents
 * satisfy the OR-gate (unlock when any parent Fork-2 passive is complete).
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see fire-spec.md § Apotheosis: Immolate
 * @see design-decisions.md § Apotheosis gate semantics (OR-gate)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Pixel distance threshold for trail-zone creation (1 tile = 8 px). */
const TRAIL_TILE_PX = 8;

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ImmolateZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly dotDefId: string;
  readonly dotIntensity: number;
  readonly tickIntervalMs: number;
  readonly sourceSkillId: string;
}

export interface ImmolateDeps {
  /**
   * Apply a buff to the player entity.
   * Immolate uses this to apply the `invincibility` status for its duration.
   * Unlike enemy status effects, player buffs are applied through a dedicated
   * player-side path — ZoneSystem DoT immunity is separate.
   */
  readonly applyPlayerStatus: (
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;
  /** Returns the player's current world position in pixels. */
  readonly getPlayerPosition: () => { x: number; y: number };
  readonly breakHide: () => void;
  readonly zones: {
    create(config: ImmolateZoneConfig): string;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ImmolateConfig {
  /** Post-expiry cooldown in ms. Very long tier = 4000ms. */
  readonly cooldownMs: number;
  /** Transformation duration in ms. */
  readonly durationMs: number;
  /** Trail zone radius in tiles. */
  readonly trailRadiusTiles: number;
  /** Trail zone duration in ms. */
  readonly trailDurationMs: number;
  /** Trail zone burning DoT intensity per tick. */
  readonly trailDotIntensity: number;
  /** Trail zone DoT tick interval in ms. */
  readonly trailTickIntervalMs: number;
  /** Movement threshold in pixels before spawning a trail zone (1 tile). */
  readonly trailTileThresholdPx: number;
  /** Burst zone radius in tiles. */
  readonly burstRadiusTiles: number;
  /** Burst zone duration in ms. */
  readonly burstDurationMs: number;
  /** Burst zone burning DoT intensity per tick. */
  readonly burstDotIntensity: number;
  /** Burst zone DoT tick interval in ms. */
  readonly burstTickIntervalMs: number;
}

const DEFAULT_CONFIG: ImmolateConfig = {
  cooldownMs: 4000,           // very_long cooldown tier
  durationMs: 10000,          // 10 seconds of living flame
  trailRadiusTiles: 2,        // spec: 2-tile radius trail zones
  trailDurationMs: 4000,      // spec: 4000ms trail zone duration
  trailDotIntensity: 2,       // spec: burning intensity 2
  trailTickIntervalMs: 500,   // spec: 500ms tick interval
  trailTileThresholdPx: TRAIL_TILE_PX,
  burstRadiusTiles: 4,        // spec: 4-tile radius burst
  burstDurationMs: 2000,      // spec: 2000ms burst duration
  burstDotIntensity: 4,       // spec: burning intensity 4
  burstTickIntervalMs: 500,   // spec: 500ms tick interval
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ImmolateSkillEffect implements SkillEffect {
  readonly skillId = 'immolate';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ImmolateDeps;
  private readonly config: ImmolateConfig;

  private _cooldownRemaining = 0;
  private _activeTimeRemaining = 0;
  private _isActiveFlag = false;
  /** Position at which the most recent trail zone was anchored. */
  private _lastTrailPos: { x: number; y: number } | null = null;
  private _activations = 0;
  private _trailZonesCreated = 0;

  constructor(deps: ImmolateDeps, config: Partial<ImmolateConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get trailZonesCreated(): number { return this._trailZonesCreated; }

  isActive(): boolean { return this._isActiveFlag; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._isActiveFlag;
  }

  // ---------------------------------------------------------------------------
  // Activation
  // ---------------------------------------------------------------------------

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.deps.breakHide();

    // Apply player invincibility for the full transformation duration.
    // The game layer's damage pipeline checks for this buff and negates hits.
    this.deps.applyPlayerStatus('invincibility', {
      durationMs: this.config.durationMs,
      sourceSkillId: 'immolate',
    });

    this._isActiveFlag = true;
    this._activeTimeRemaining = this.config.durationMs;
    // Anchor to current position — no trail zone until the player moves.
    this._lastTrailPos = this.deps.getPlayerPosition();
    this._activations++;
    return true;
  }

  // ---------------------------------------------------------------------------
  // Deactivation (early cancel or external interrupt)
  // ---------------------------------------------------------------------------

  deactivate(): void {
    if (!this._isActiveFlag) return;
    this._expire();
  }

  // ---------------------------------------------------------------------------
  // Per-frame update
  // ---------------------------------------------------------------------------

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (!this._isActiveFlag) return;

    // Trail logic runs before time decrement so the player's position at the
    // final tick is still sampled before expiry fires.
    this._updateTrail();

    this._activeTimeRemaining -= delta;
    if (this._activeTimeRemaining <= 0) {
      this._expire();
    }
  }

  // ---------------------------------------------------------------------------
  // Expiry (natural timeout or early cancel)
  // ---------------------------------------------------------------------------

  private _expire(): void {
    // Burst zone at current player position.
    const pos = this.deps.getPlayerPosition();
    this.deps.zones.create({
      id: `immolate-burst-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'fire_zone',
      position: { x: pos.x, y: pos.y },
      shape: 'circle',
      radiusTiles: this.config.burstRadiusTiles,
      durationMs: this.config.burstDurationMs,
      dotDefId: 'burning',
      dotIntensity: this.config.burstDotIntensity,
      tickIntervalMs: this.config.burstTickIntervalMs,
      sourceSkillId: 'immolate',
    });

    this._isActiveFlag = false;
    this._activeTimeRemaining = 0;
    this._lastTrailPos = null;
    this._cooldownRemaining = this.config.cooldownMs;
  }

  // ---------------------------------------------------------------------------
  // Trail zone logic
  // ---------------------------------------------------------------------------

  private _updateTrail(): void {
    if (!this._lastTrailPos) return;

    const currentPos = this.deps.getPlayerPosition();
    const dx = currentPos.x - this._lastTrailPos.x;
    const dy = currentPos.y - this._lastTrailPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < this.config.trailTileThresholdPx) return;

    // Create trail zone at the previous anchor — the fire stays where we were,
    // not where we are (the trail marks the path already walked).
    this.deps.zones.create({
      id: `immolate-trail-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'fire_zone',
      position: { x: this._lastTrailPos.x, y: this._lastTrailPos.y },
      shape: 'circle',
      radiusTiles: this.config.trailRadiusTiles,
      durationMs: this.config.trailDurationMs,
      dotDefId: 'burning',
      dotIntensity: this.config.trailDotIntensity,
      tickIntervalMs: this.config.trailTickIntervalMs,
      sourceSkillId: 'immolate',
    });

    this._lastTrailPos = { x: currentPos.x, y: currentPos.y };
    this._trailZonesCreated++;
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  destroy(): void {
    // Destroyed without expiry (e.g., scene teardown) — no burst.
    this._isActiveFlag = false;
    this._cooldownRemaining = 0;
    this._activeTimeRemaining = 0;
    this._lastTrailPos = null;
  }
}
